import React, { useEffect, useState } from 'react';
import { ArrowUp, Plus, Square } from 'lucide-react';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';

const formatTimer = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

const WAVE_BARS = 32;

export const VoiceDictationBar = ({
  listening,
  onStop,
  onSend,
  onAdd,
  canSend = true,
  className = ''
}) => {
  const { t } = useI18n();
  const [elapsed, setElapsed] = useState(0);
  const [showStopTip, setShowStopTip] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!listening) {
      setElapsed(0);
      return undefined;
    }
    setElapsed(0);
    const id = window.setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => window.clearInterval(id);
  }, [listening]);

  useEffect(() => {
    if (!listening) return undefined;
    const id = window.setInterval(() => setTick((value) => value + 1), 120);
    return () => window.clearInterval(id);
  }, [listening]);

  useEffect(() => {
    if (!listening) return undefined;
    setShowStopTip(true);
    const id = window.setTimeout(() => setShowStopTip(false), 2500);
    return () => window.clearTimeout(id);
  }, [listening]);

  if (!listening) return null;

  return (
    <div className={classNames('w-full', className)}>
      <div className="relative flex items-center gap-2 rounded-xl border border-border bg-white px-2 py-2 shadow-sm dark:bg-panel-strong">
        <button
          type="button"
          onClick={onSend}
          disabled={!canSend}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-950 text-white transition enabled:hover:bg-neutral-800 disabled:opacity-40 dark:bg-white dark:text-neutral-950"
          aria-label={t('send')}
        >
          <ArrowUp size={16} />
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={onStop}
            className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600"
            aria-label={t('chatStopDictation')}
          >
            <span className="absolute inset-0 animate-ping rounded-full bg-red-400/50" />
            <Square size={12} fill="currentColor" className="relative" />
          </button>
          {showStopTip ? (
            <span className="absolute start-1/2 top-full z-10 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-neutral-800 px-2 py-1 text-[10px] text-white dark:bg-neutral-700">
              {t('chatStopDictation')}
            </span>
          ) : null}
        </div>

        <div className="mx-2 flex h-8 min-w-0 flex-1 items-center justify-center gap-[3px] overflow-hidden">
          {Array.from({ length: WAVE_BARS }).map((_, index) => {
            const phase = (tick + index * 2) % 10;
            const height = 4 + ((phase * 7 + (index % 5) * 3) % 18);
            return (
              <span
                key={index}
                className="w-[2px] shrink-0 rounded-full bg-neutral-800 transition-[height] duration-100 ease-out dark:bg-neutral-200"
                style={{ height: `${height}px` }}
              />
            );
          })}
        </div>

        <span className="inline-flex shrink-0 items-center gap-1.5 tabular-nums text-xs text-neutral-500">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
          {formatTimer(elapsed)}
        </span>

        <button
          type="button"
          onClick={onAdd}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-neutral-500 transition hover:bg-neutral-200 dark:hover:bg-neutral-800"
          aria-label={t('chatToolAttach')}
        >
          <Plus size={16} />
        </button>
      </div>
    </div>
  );
};
